import {Keypair, PublicKey} from "@solana/web3.js"
import {getED25519Key} from "@toruslabs/openlogin-ed25519"
import {useCallback, useEffect, useState} from "react"
import nacl from "tweetnacl"
import {getHashedEd25519Keypair, initOpenLogin} from "../functions/auth"
import {Keys} from "../interfaces/keys"
import {AuthMethod} from "../types"

export const useAuth = () => {
  const [authMethod, setAuthMethod] = useState<AuthMethod>()
  const [keys, setKeys] = useState<Keys | null>()

  const initWeb3Auth = useCallback(async (): Promise<Keys | null> => {
    const openLogin = await initOpenLogin()
    const privateKey = openLogin.privKey
    if (!privateKey) {
      return null
    }
    const {sk} = getED25519Key(privateKey)
    const pk = Keypair.fromSecretKey(sk).publicKey.toBase58()

    const ipfsKeys = await getHashedEd25519Keypair(privateKey)

    return {
      pk,
      sk: privateKey,
      webIpfs: ipfsKeys.webKeypair,
      desktopIpfs: ipfsKeys.desktopKeypair,
    }
  }, [])

  const initPhantom = useCallback(async (signedMsg: string): Promise<Keys | null> => {
    const parsed = JSON.parse(signedMsg)
    const provider = (window as any).phantom?.solana

    let pubKey: Uint8Array
    try {
      const resp = await provider.connect()
      pubKey = resp.publicKey.toBuffer()
    } catch (e) {
      return null
    }

    if (!pubKey) {
      return null
    }

    const signature = Uint8Array.from(parsed.signature.data)
    const isVerified = nacl.sign.detached.verify(
      new TextEncoder().encode("Sign in on DeGit"),
      signature,
      pubKey
    )

    if (!isVerified) {
      return null
    }

    const privateKey = Buffer.from(signature).toString("hex")
    const ipfsKeys = await getHashedEd25519Keypair(privateKey)

    return {
      pk: new PublicKey(pubKey).toBase58(),
      sk: privateKey,
      webIpfs: ipfsKeys.webKeypair,
      desktopIpfs: ipfsKeys.desktopKeypair,
    }
  }, [])

  const initAuth = useCallback(async () => {
    // Access localStorage to check hashed key
    const method = localStorage.getItem("method") as AuthMethod
    if (method !== "phantom" && method !== "web3auth") {
      setKeys(null)
      return null
    }

    // Check if signed message is valid
    const signedMessage = localStorage.getItem("signedMessage")
    if (method === "phantom" && !signedMessage) {
      setKeys(null)
      return null
    }

    let keys: Keys | null
    switch (method) {
      case "web3auth":
        setAuthMethod("web3auth")
        keys = await initWeb3Auth()
        break
      case "phantom":
        setAuthMethod("phantom")
        keys = await initPhantom(signedMessage!)
        break
      default:
        keys = null
    }

    setKeys(keys)
  }, [initWeb3Auth, initPhantom])

  useEffect(() => {
    initAuth().then()
  }, [initAuth])

  return {
    authMethod,
    keys,
  }
}
